import { useAppSelector } from "../../../../../store/hooks";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { updateStructureComponent } from "../../../../../store/reducers/builderReducers/structure/structureActions";
import { useEffect, useState, useRef } from "react";
import React from "react";
import { fetchApiGetUuid } from "../../../../../api/api";
import { EventRoundTripComponent } from "../../../../_default/interfaces/base";
import "../EventsMenu/EventsMenu.scss";
import { Pages } from "../../../../../store/reducers/builderReducers/pages/pagesApi";

interface Props {
  id: number;
  triggerResize: () => void;
}

interface EventGroup {
  id: number;
  hash: string;
  groupName: string;
  hasTickets: boolean;
  location: string;
}

export default function EventRoundTripMenu({ id, triggerResize }: Props) {
  const component = useAppSelector(
    (state) => state.builder.structure.components[id]
  )! as EventRoundTripComponent;

  const webpages = useAppSelector(
    (state) => state.builder.pages.list as Pages["list"]
  );
  const languages = useAppSelector((state) => state.builder.languageSettings);

  // Group 1 state
  const [group1, setGroup1] = useState<EventGroup | null>(null);
  const [searchQuery1, setSearchQuery1] = useState("");
  const [filteredGroups1, setFilteredGroups1] = useState<EventGroup[]>([]);
  const [isSearching1, setIsSearching1] = useState(false);
  const [loading1, setLoading1] = useState(false);
  const [error1, setError1] = useState<string | null>(null);
  const [isMenuClicked1, setIsMenuClicked1] = useState(false);
  const searchRef1 = useRef<HTMLInputElement>(null);
  const searchTimeout1 = useRef<NodeJS.Timeout | null>(null);

  // Group 2 state
  const [group2, setGroup2] = useState<EventGroup | null>(null);
  const [searchQuery2, setSearchQuery2] = useState("");
  const [filteredGroups2, setFilteredGroups2] = useState<EventGroup[]>([]);
  const [isSearching2, setIsSearching2] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [error2, setError2] = useState<string | null>(null);
  const [isMenuClicked2, setIsMenuClicked2] = useState(false);
  const searchRef2 = useRef<HTMLInputElement>(null);
  const searchTimeout2 = useRef<NodeJS.Timeout | null>(null);

  // Redirect settings
  const [redirectPageId, setRedirectPageId] = useState<number | null>(
    component.attributes.add_to_cart_redirect_page_id || null
  );
  const [redirectUrl, setRedirectUrl] = useState<string>(
    component.attributes.add_to_cart_redirect_url || ""
  );

  // Initialize from saved attributes
  useEffect(() => {
    if (component.attributes.event_group_hash_1) {
      fetchGroupByHash(component.attributes.event_group_hash_1, 1);
    }
    if (component.attributes.event_group_hash_2) {
      fetchGroupByHash(component.attributes.event_group_hash_2, 2);
    }
    triggerResize();
  }, []);

  const fetchGroupByHash = async (groupHash: string, groupNum: 1 | 2) => {
    try {
      if (groupNum === 1) {
        setLoading1(true);
        setError1(null);
      } else {
        setLoading2(true);
        setError2(null);
      }

      const response = await fetchApiGetUuid<EventGroup>(
        `/events-groups/${groupHash}`
      );

      if (response) {
        if (groupNum === 1) {
          setGroup1(response);
          setSearchQuery1(response.groupName);
        } else {
          setGroup2(response);
          setSearchQuery2(response.groupName);
        }
      }

      if (groupNum === 1) setLoading1(false);
      else setLoading2(false);
    } catch (err) {
      console.error(`Error fetching event group ${groupNum}:`, err);
      if (groupNum === 1) {
        setError1("Error fetching event group");
        setLoading1(false);
      } else {
        setError2("Error fetching event group");
        setLoading2(false);
      }
    }
  };

  const searchEventGroups = async (query: string, groupNum: 1 | 2) => {
    try {
      if (groupNum === 1) {
        setLoading1(true);
        setError1(null);
      } else {
        setLoading2(true);
        setError2(null);
      }

      const response = await fetchApiGetUuid<any>(
        `/events-groups?name=${encodeURIComponent(query)}`
      );

      let groups: EventGroup[] = [];
      if (Array.isArray(response)) {
        groups = response;
      } else if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        Array.isArray(response.data)
      ) {
        groups = response.data;
      }

      if (groupNum === 1) {
        setFilteredGroups1(groups);
        setLoading1(false);
      } else {
        setFilteredGroups2(groups);
        setLoading2(false);
      }
    } catch (err) {
      console.error(`Error searching event groups for group ${groupNum}:`, err);
      if (groupNum === 1) {
        setError1("Failed to load event groups");
        setLoading1(false);
      } else {
        setError2("Failed to load event groups");
        setLoading2(false);
      }
    }
  };

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    groupNum: 1 | 2
  ) => {
    const value = e.target.value;
    if (groupNum === 1) {
      setSearchQuery1(value);
      if (searchTimeout1.current) clearTimeout(searchTimeout1.current);
      searchTimeout1.current = setTimeout(() => {
        searchEventGroups(value, 1);
      }, 300);
    } else {
      setSearchQuery2(value);
      if (searchTimeout2.current) clearTimeout(searchTimeout2.current);
      searchTimeout2.current = setTimeout(() => {
        searchEventGroups(value, 2);
      }, 300);
    }
  };

  const handleSearchFocus = (groupNum: 1 | 2) => {
    if (groupNum === 1) {
      setIsSearching1(true);
      if (!group1) searchEventGroups(searchQuery1, 1);
    } else {
      setIsSearching2(true);
      if (!group2) searchEventGroups(searchQuery2, 2);
    }
    triggerResize();
  };

  const handleSearchBlur = (groupNum: 1 | 2) => {
    const isMenuClicked = groupNum === 1 ? isMenuClicked1 : isMenuClicked2;
    if (!isMenuClicked) {
      setTimeout(() => {
        if (groupNum === 1) setIsSearching1(false);
        else setIsSearching2(false);
        triggerResize();
      }, 200);
    }
    if (groupNum === 1) setIsMenuClicked1(false);
    else setIsMenuClicked2(false);
  };

  const handleSelectGroup = (group: EventGroup, groupNum: 1 | 2) => {
    if (groupNum === 1) {
      setGroup1(group);
      setSearchQuery1(group.groupName);
      setIsSearching1(false);
      updateStructureComponent(id, "attributes.event_group_hash_1", group.hash);
      updateStructureComponent(
        id,
        "attributes.event_group_name_1",
        group.groupName
      );
    } else {
      setGroup2(group);
      setSearchQuery2(group.groupName);
      setIsSearching2(false);
      updateStructureComponent(id, "attributes.event_group_hash_2", group.hash);
      updateStructureComponent(
        id,
        "attributes.event_group_name_2",
        group.groupName
      );
    }
    triggerResize();
  };

  const handleClearSelection = (e: React.MouseEvent, groupNum: 1 | 2) => {
    e.stopPropagation();
    if (groupNum === 1) {
      setGroup1(null);
      setSearchQuery1("");
      updateStructureComponent(id, "attributes.event_group_hash_1", null);
      updateStructureComponent(id, "attributes.event_group_name_1", null);
    } else {
      setGroup2(null);
      setSearchQuery2("");
      updateStructureComponent(id, "attributes.event_group_hash_2", null);
      updateStructureComponent(id, "attributes.event_group_name_2", null);
    }
  };

  const renderGroupSearch = (groupNum: 1 | 2) => {
    const group = groupNum === 1 ? group1 : group2;
    const searchQuery = groupNum === 1 ? searchQuery1 : searchQuery2;
    const isSearching = groupNum === 1 ? isSearching1 : isSearching2;
    const loading = groupNum === 1 ? loading1 : loading2;
    const error = groupNum === 1 ? error1 : error2;
    const filteredGroups = groupNum === 1 ? filteredGroups1 : filteredGroups2;
    const searchRef = groupNum === 1 ? searchRef1 : searchRef2;

    return (
      <div
        className="d-flex flex-wrap align-items-center"
        style={{ marginTop: "3px" }}
      >
        <span>
          {groupNum === 1 ? "Group 1 (depart):" : "Group 2 (return):"}
        </span>
        <div
          className="b-mx-1 content-width-menu_input position-relative"
          style={{ width: "200px" }}
        >
          <div
            className="d-flex align-items-center"
            style={{
              borderRadius: "4px",
              padding: "2px 4px",
              width: "100%",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (groupNum === 1) setIsMenuClicked1(true);
              else setIsMenuClicked2(true);
              searchRef.current?.focus();
            }}
          >
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e, groupNum)}
              onFocus={() => handleSearchFocus(groupNum)}
              onBlur={() => handleSearchBlur(groupNum)}
              placeholder={group ? "" : "Search for event groups..."}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                width: "100%",
                outline: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            />
            {group ? (
              <ClearIcon
                style={{
                  fontSize: "16px",
                  marginLeft: "4px",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
                onClick={(e) => handleClearSelection(e, groupNum)}
              />
            ) : (
              <SearchIcon
                style={{
                  fontSize: "16px",
                  marginLeft: "4px",
                  flexShrink: 0,
                }}
              />
            )}
          </div>

          {isSearching && (
            <div
              style={{
                position: "absolute",
                width: "100%",
                maxHeight: "200px",
                overflowY: "auto",
                top: "24px",
                background: "#323232",
                borderRadius: "2px",
                zIndex: 1000,
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                if (groupNum === 1) setIsMenuClicked1(true);
                else setIsMenuClicked2(true);
              }}
            >
              {loading ? (
                <div className="dropdown-item builder-dropdown__item">
                  Loading event groups...
                </div>
              ) : error ? (
                <div className="dropdown-item builder-dropdown__item">
                  {error}
                </div>
              ) : filteredGroups.length > 0 ? (
                filteredGroups.map((g) => (
                  <div
                    key={g.id}
                    className={
                      "dropdown-item builder-dropdown__item" +
                      (group?.id === g.id
                        ? " builder-dropdown__item--active"
                        : "")
                    }
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectGroup(g, groupNum);
                    }}
                  >
                    {g.groupName}
                  </div>
                ))
              ) : (
                <div className="dropdown-item builder-dropdown__item">
                  No event groups found
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="d-flex flex-column">
      {renderGroupSearch(1)}
      {renderGroupSearch(2)}

    </div>
  );
}
